// 23. 10. 2026 (owner): "if there is a new message, show me some notification". The crewmates' messages since the Chat tab
// was last open come in /api/me (`crewChatUnread`; the server leaves out the player's own, blocked players' and the camp
// radio's): a dot on the Crew menu entry (the sidebar, the More sheet and the phone's More button) and a count on the Chat
// tab. The Chat tab open and in view marks everything read (`POST /api/clans/chat/read`); the server keeps the time, so the
// dot stays gone after a reload and on the player's other devices (`clan:read`). No push notification for the chat.

import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MeDTO } from '@lastday/shared';
import { api } from '@/api/client';
import { qk, useMe } from '@/api/hooks';

/** Unread crewmates' messages (0 without a crew or while crews are off). */
export function useCrewChatUnread(): number {
  const me = useMe().data;
  return me?.features.groups === false ? 0 : (me?.survivor?.crewChatUnread ?? 0);
}

/** Marks the crew chat read: the dot and the count go at once, the server keeps it. */
export function useMarkChatRead(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    qc.setQueryData<MeDTO>(qk.me, (m) => (m?.survivor?.crewChatUnread ? { ...m, survivor: { ...m.survivor, crewChatUnread: 0 } } : m));
    void api.post('/clans/chat/read').catch(() => undefined);
  }, [qc]);
}

/** Whether the crew chat is open and in view now: a new message is read there (no dot, the chat marks it read). */
export function crewChatInView(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible' && window.location.pathname.startsWith('/crew/chat');
}

const subscribeVisible = (l: () => void) => {
  document.addEventListener('visibilitychange', l);
  return () => document.removeEventListener('visibilitychange', l);
};
/** Whether the page is in view (not a hidden tab or a phone's background). */
export const usePageVisible = () => useSyncExternalStore(subscribeVisible, () => document.visibilityState === 'visible', () => true);
